import { ChevronLeft } from "@/components/ui/icons";
import { router, useLocalSearchParams } from "expo-router";
import { useEffect, useMemo, useRef, useState } from "react";
import { Image, Linking, Pressable, Text, useWindowDimensions, View } from "react-native";
import { useSafeAreaInsets } from "react-native-safe-area-context";

import { BrowseSearchBar } from "@/components/browse/BrowseSearchBar";
import { ShowHallStrip } from "@/components/floor-plan/ShowHallStrip";
import { PublicShowSkeleton } from "@/components/shows/PublicShowSkeleton";
import { Badge, ConditionChip } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { FlashList } from "@/components/ui/FlashList";
import { Screen } from "@/components/ui/Screen";
import { APP_BASE_URL } from "@/config";
import { useMarkInteractive } from "@/hooks/useMarkInteractive";
import { usePublicShow } from "@/hooks/usePublicShow";
import { publicShowVendorStorefrontUrl, usePublicShowVendor } from "@/hooks/usePublicShowInventory";
import { loadShowAnalytics } from "@/lib/browse/showAnalyticsClient";
import { formatCardIdentity, formatGradeLabel } from "@/lib/catalog/cardIdentity";
import { buylistPath } from "@/lib/shop/publicUrls";
import { parseFloorPlan } from "@/lib/shows/floorPlan";
import { sortCardCopies, treatmentLabel } from "@/lib/shows/showCardFinding";
import { describeShowLocation, shouldCollapseShowMap } from "@/lib/shows/showWayfinding";
import { useThemeColors } from "@/theme/theme";

const one = (value: string | string[] | undefined) => Array.isArray(value) ? value[0] : value;
const atTable = (numbers: readonly string[]) =>
  numbers.length ? `at table ${numbers.join(", ")}` : "with no table assigned";

export default function PublicShowVendorScreen() {
  const params = useLocalSearchParams<
    { participationId: string; q?: string; showId: string; storefront?: string; }
  >();
  const showId = one(params.showId);
  const participationId = one(params.participationId);
  const query = one(params.q) ?? "";
  // Where the storefront handoff sends an attendee back to when Show Mode stopped under them.
  const handoffClosed = one(params.storefront) === "closed";
  const insets = useSafeAreaInsets();
  const { fontScale } = useWindowDimensions();
  const c = useThemeColors();
  const showQuery = usePublicShow(showId);
  const vendorQuery = usePublicShowVendor(showId, participationId);
  // Deep link straight to one vendor at a show — both queries have to settle before the customer
  // knows which table to walk to, which is the whole point of the page.
  useMarkInteractive(!showQuery.isLoading && !vendorQuery.isLoading);
  const tracked = useRef(false);

  const plan = useMemo(() => {
    const source = showQuery.show?.plan?.document;
    if (!source) return null;
    try {
      return parseFloorPlan(typeof source === "string" ? source : JSON.stringify(source));
    } catch {
      return null;
    }
  }, [showQuery.show?.plan?.document]);
  const tableIds = useMemo(() => {
    const wanted = new Set(vendorQuery.data?.table_numbers ?? []);
    return plan?.tables.filter((table) => wanted.has(table.number)).map((table) => table.id) ?? [];
  }, [plan, vendorQuery.data?.table_numbers]);
  const groups = useMemo(
    () => [{ participationId: participationId ?? "", tableIds }],
    [participationId, tableIds],
  );
  const mapCollapsed = !!plan && shouldCollapseShowMap(plan, groups, fontScale);
  const [cardQuery, setCardQuery] = useState("");
  // A Vendor who brought 500 cards is a 500-row page, so the list needs a way in as well as
  // virtualization. Client-side and undebounced: the whole published list is already in hand.
  const cards = useMemo(() => {
    const all = vendorQuery.data?.cards ?? [];
    const needle = cardQuery.trim().toLocaleLowerCase();
    if (!needle) return all;
    return all.filter(({ card }) =>
      [card.name, card.set_name, card.collector_number].some((value) =>
        value?.toLocaleLowerCase().includes(needle)
      )
    );
  }, [cardQuery, vendorQuery.data?.cards]);

  useEffect(() => {
    const show = showQuery.show;
    const vendor = vendorQuery.data;
    if (!show || !vendor || tracked.current) return;
    tracked.current = true;
    const showEndsAt = (show.sessions ?? []).map((session) => session.ends_at).sort().at(-1)
      ?? null;
    void loadShowAnalytics({ showId: show.public_id, showEndsAt }).then((analytics) => {
      analytics.vendorOpened(
        show.public_id,
        vendor.participation_id,
        vendor.table_numbers[0] ?? "unassigned",
      );
    });
  }, [showQuery.show, vendorQuery.data]);

  const back = () => {
    if (router.canGoBack()) router.back();
    else {
      const suffix = query ? `?${new URLSearchParams({ q: query })}` : "";
      router.replace(`/shows/${encodeURIComponent(showId ?? "")}${suffix}`);
    }
  };

  const openStorefront = () => {
    if (!showId || !participationId) return;
    void Linking.openURL(publicShowVendorStorefrontUrl(showId, participationId, query));
  };

  if (showQuery.isLoading || vendorQuery.isLoading) {
    return <PublicShowSkeleton label="Loading Vendor inventory…" />;
  }

  if (!showQuery.show || !vendorQuery.data || showQuery.error || vendorQuery.error) {
    return (
      <Screen className="events-page items-center justify-center px-6" role="main">
        <Text className="text-lg font-title text-foreground">Vendor listing unavailable</Text>
        <Text className="mt-2 text-center font-body text-muted-foreground">
          This Vendor may no longer be participating in the Show.
        </Text>
        <View className="mt-5 flex-row gap-2">
          <Button title="Back to Show" variant="outline" onPress={back} />
          <Button title="Try again" variant="outline" onPress={() => void vendorQuery.refetch()} />
        </View>
      </Screen>
    );
  }

  const vendor = vendorQuery.data;
  const buylistSlug = showQuery.show.roster.find((row) =>
    row.participation_id === vendor.participation_id
  )?.buylist_slug;
  const location = plan ? describeShowLocation(plan, tableIds) : null;

  return (
    <Screen className="events-page" role="main">
      {/* Every Vendor on the events host shared one tab and link-preview title (TRO-905). */}
      <View
        className="w-full max-w-[760px] self-center flex-row items-center gap-2 border-b border-border px-2 pb-3"
        style={{ paddingTop: insets.top + 8 }}
      >
        <Pressable
          accessibilityRole="button"
          accessibilityLabel="Back to card results"
          onPress={back}
          className="h-11 w-11 items-center justify-center rounded-full active:bg-surface-muted"
        >
          <ChevronLeft size={22} color={c.foreground} />
        </Pressable>
        <View className="min-w-0 flex-1">
          <Text className="font-title text-foreground" numberOfLines={1}>
            {showQuery.show.name}
          </Text>
          <Text className="text-xs font-body text-muted-foreground" numberOfLines={1}>
            Back to card results
          </Text>
        </View>
      </View>

      {
        /* A FlashList, not a ScrollView: `vendor.cards.map` mounted every published card at once,
        so a 500-card Vendor built ~44,000pt of views on a phone browser before first paint.
        Everything above the rows is the list's own header, which also keeps the search box mounted
        through the keystroke that empties the list. */
      }
      <FlashList
        className="w-full max-w-[760px] flex-1 self-center"
        data={cards}
        keyExtractor={({ card }) => card.card_id}
        ItemSeparatorComponent={() => <View className="h-2" />}
        contentContainerStyle={{ padding: 16, paddingBottom: insets.bottom + 32 }}
        ListHeaderComponent={
          <View className="gap-4 pb-2">
            <View>
              <Text
                accessibilityRole="header"
                aria-level={1}
                className="text-2xl font-title text-foreground"
              >
                {vendor.name}
              </Text>
              <Text className="mt-1 font-body text-muted-foreground">
                {atTable(vendor.table_numbers)}
                {location ? ` · ${location}` : ""}
              </Text>
            </View>

            <View className="gap-3 rounded-xl border border-primary bg-accent-soft p-4">
              <View>
                <Text className="font-title text-foreground">Shop this Vendor</Text>
                <Text
                  accessibilityRole={!vendor.storefront_available && handoffClosed
                    ? "alert"
                    : undefined}
                  className="mt-1 font-body text-muted-foreground"
                >
                  {vendor.storefront_available
                    ? "See prices and request cards in their table storefront. This Show list stays read-only."
                    : handoffClosed
                    ? `${vendor.name} just closed their storefront. Prices are at the table until they start Show Mode again.`
                    : `Prices and card requests open when ${vendor.name} starts Show Mode.`}
                </Text>
              </View>
              {vendor.storefront_available
                ? (
                  <Button
                    title="View prices & request"
                    onPress={openStorefront}
                    className="self-start"
                  />
                )
                : null}
            </View>

            {buylistSlug
              ? (
                <Button
                  title="✦ Buying · See this Vendor's Buylist"
                  accessibilityRole="link"
                  variant="outline"
                  onPress={() =>
                    void Linking.openURL(
                      `${APP_BASE_URL}${buylistPath(buylistSlug)}?show=${
                        encodeURIComponent(showId ?? "")
                      }`,
                    )}
                />
              )
              : null}

            {showQuery.show.phase === "upcoming"
              ? (
                <Text className="font-body text-xs leading-4 text-warning">
                  Inventory may change before doors open. Vendors keep selling from this stock until
                  then.
                </Text>
              )
              : null}

            {plan && !mapCollapsed && tableIds.length
              ? <ShowHallStrip document={plan} groups={groups} />
              : (
                <View
                  testID="show-location"
                  className="rounded-xl border border-border bg-surface p-4"
                >
                  <Text className="font-title text-foreground">
                    {atTable(vendor.table_numbers)}
                  </Text>
                  <Text className="mt-1 font-body text-muted-foreground">
                    {mapCollapsed
                      ? "At this text size, this location is clearer as words."
                      : "The floor view is unavailable, but the assigned tables remain listed."}
                  </Text>
                </View>
              )}

            <View className="gap-2">
              <Text
                accessibilityRole="header"
                aria-level={2}
                className="text-lg font-title text-foreground"
              >
                {cardQuery.trim() && vendor.cards.length
                  ? `${cards.length} of ${vendor.cards.length} cards published`
                  : `${vendor.cards.length} ${
                    vendor.cards.length === 1 ? "card" : "cards"
                  } published`}
              </Text>
              {vendor.cards.length
                ? (
                  <>
                    <Text className="font-body text-sm text-muted-foreground">
                      A read-only list of what this Vendor brought. Quantities can change during the
                      Show.
                    </Text>
                    <BrowseSearchBar value={cardQuery} onChange={setCardQuery} />
                  </>
                )
                : null}
            </View>
          </View>
        }
        ListEmptyComponent={
          <View
            className="rounded-xl border border-border bg-surface p-4"
            accessibilityLiveRegion="polite"
          >
            {vendor.cards.length
              ? (
                <>
                  <Text className="font-title text-foreground">No cards match</Text>
                  <Text className="mt-1 font-body text-muted-foreground">
                    Nothing in this Vendor's Show list matches that. Try another name or number.
                  </Text>
                </>
              )
              : (
                <>
                  <Text className="font-title text-foreground">No public cards right now</Text>
                  <Text className="mt-1 font-body text-muted-foreground">
                    {vendor.name} is still in the Vendor roster {atTable(vendor.table_numbers)}.
                  </Text>
                </>
              )}
          </View>
        }
        renderItem={({ item: { card, copies } }) => {
          const identity = formatCardIdentity(
            card.set_name,
            card.collector_number,
            treatmentLabel(card),
          );
          return (
            <View
              testID="show-vendor-card"
              className="min-h-20 gap-3 rounded-xl border border-border bg-surface p-3"
            >
              <View className="flex-row items-center gap-3">
                {card.image_url
                  ? (
                    <Image
                      source={{ uri: card.image_url }}
                      className="h-14 w-10 rounded-md bg-surface-muted"
                      resizeMode="cover"
                      accessible={false}
                      accessibilityIgnoresInvertColors
                    />
                  )
                  : null}
                <View className="min-w-0 flex-1 gap-1">
                  <Text className="font-title text-foreground" numberOfLines={2}>
                    {card.name}
                  </Text>
                  {identity
                    ? (
                      <Text className="text-xs font-body text-muted-foreground">
                        {identity}
                      </Text>
                    )
                    : null}
                  <View className="flex-row flex-wrap gap-1">
                    {sortCardCopies(copies).map((copy, index) => {
                      const grade = formatGradeLabel(
                        copy.grading_company,
                        copy.grade,
                        copy.is_graded,
                      );
                      return copy.condition
                        ? (
                          <ConditionChip
                            key={`${copy.condition}-${index}`}
                            condition={`${copy.condition} ×${copy.quantity}`}
                          />
                        )
                        : (
                          <Badge
                            key={`${grade}-${index}`}
                            label={`${grade ?? "Condition not listed"} ×${copy.quantity}`}
                            className="bg-surface-muted"
                            textClassName="text-muted-foreground"
                          />
                        );
                    })}
                  </View>
                </View>
              </View>
            </View>
          );
        }}
      />
    </Screen>
  );
}
